import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Chip,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Input,
  Snackbar,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import { motion } from 'framer-motion';

const sampleJobs = [
  { id: 1, title: 'Senior Software Engineer', location: 'Neyveli / Remote', type: 'Full-time', team: 'Platform', remote: true, tags: ['Java', 'Spring', 'Microservices'] },
  { id: 2, title: 'Data Scientist', location: 'Neyveli', type: 'Full-time', team: 'Analytics', remote: false, tags: ['Python', 'ML', 'Spark'] },
  { id: 3, title: 'Frontend Developer', location: 'Remote', type: 'Contract', team: 'Web', remote: true, tags: ['React', 'MUI', 'TypeScript'] }
];

function JobCard({ job, onApply }) {
  return (
    <motion.div whileHover={{ y: -6 }} style={{ height: '100%' }}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main' }}>{job.title.charAt(0)}</Avatar>
            <Box>
              <Typography variant="h6">{job.title}</Typography>
              <Typography variant="body2" color="text.secondary">{job.location} • {job.type}</Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {job.tags.map(t => <Chip key={t} label={t} size="small" />)}
          </Box>
        </CardContent>

        <Box sx={{ p: 2, pt: 0 }}>
          <Button variant="contained" fullWidth onClick={() => onApply(job)}>Apply</Button>
        </Box>
      </Card>
    </motion.div>
  );
}

export default function CareersClean() {
  const jobs = useMemo(() => sampleJobs, []);
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  function handleOpen(job) {
    setSelectedJob(job);
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
    setSelectedJob(null);
    setForm({ name: '', email: '', phone: '', message: '' });
    setResume(null);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  function handleFile(e) {
    const file = e.target.files && e.target.files[0];
    setResume(file || null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !resume) {
      setSnack({ open: true, severity: 'error', message: 'Please provide name, email and attach your resume.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('jobId', selectedJob.id);
      fd.append('jobTitle', selectedJob.title);
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('message', form.message);
      fd.append('resume', resume);

      const res = await fetch(`${API_BASE}/api/careers/apply`, { method: 'POST', body: fd });
      if (!res.ok) {
        const text = await res.text().catch(() => 'Server error');
        throw new Error(text || 'Failed to submit application');
      }

      setSnack({ open: true, severity: 'success', message: 'Application submitted. We will contact you shortly.' });
      handleClose();
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: 'error', message: err.message || 'Failed to submit application' });
    } finally {
      setIsSubmitting(false);
    }
  }

  const visible = jobs.filter(j => (j.title + j.tags.join(' ') + j.location).toLowerCase().includes(filter.toLowerCase()));

  return (
    <Container sx={{ py: 6 }}>
      <Paper component={motion.div} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>Careers at NehaInfoTech</Typography>
        <Typography variant="body1" color="text.secondary">Join a collaborative team building cloud-first products and services. We value inclusivity, continuous learning and real impact.</Typography>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField placeholder="Search jobs, skills, location" value={filter} onChange={(e) => setFilter(e.target.value)} fullWidth />
              <Button variant="outlined" onClick={() => setFilter('')}>Clear</Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Chip label="Remote friendly" color="primary" />
              <Chip label="Diversity & Inclusion" />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {visible.map(job => (
              <Grid item xs={12} sm={6} key={job.id}>
                <JobCard job={job} onApply={handleOpen} />
              </Grid>
            ))}
            {visible.length === 0 && (
              <Grid item xs={12}>
                <Typography color="text.secondary">No jobs found. Try a different search.</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ position: 'sticky', top: 96 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6">Why work with us</Typography>
              <Box sx={{ mt: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="body2">• Competitive salary and benefits</Typography>
                  <Typography variant="body2">• Flexible work arrangements</Typography>
                  <Typography variant="body2">• Learning stipend and mentorship</Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6">Open to referrals?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Refer a friend and earn rewards when they join.</Typography>
              <Button variant="outlined" sx={{ mt: 2 }} component={Link} to="/refer">Refer Now</Button>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Apply for: {selectedJob?.title}</DialogTitle>
        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Full name" name="name" value={form.name} onChange={handleChange} required />
              <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
              <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} />
              <TextField label="Cover message" name="message" value={form.message} onChange={handleChange} multiline rows={4} />

              <div>
                <Typography variant="body2" sx={{ mb: 1 }}>Attach your resume (PDF, DOC, DOCX)</Typography>
                <Input type="file" onChange={handleFile} inputProps={{ accept: '.pdf,.doc,.docx' }} />
                {resume && (
                  <Typography variant="caption" display="block">Selected: {resume.name}</Typography>
                )}
              </div>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>{isSubmitting ? 'Submitting...' : 'Submit Application'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
